import { VFlow } from 'bold-ui'
import { usePecField } from 'components/form'
import { LoadingIndicator } from 'components/loading'
import { useUltimoMChatQuery } from 'graphql/hooks.generated'
import { MChatRiscoEnum } from 'graphql/types.generated'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { isEmpty } from 'lodash'
import React, { useMemo } from 'react'
import { calculateAge } from 'util/date/calculateAge'
import { MetaPath } from 'util/metaPath'

import { MChatRfAccordion } from './components/MChatRfAccordion'
import { ConsultaSeguimentoField } from './consulta-seguimento/consulta-seguimento-field/ConsultaSeguimentoField'
import { convertMChatRfQueryToModel } from './converter-mChatRf'
import { MChatField } from './mchat/mchat-field/MChatField'
import { MChatAnteriorFooterInfoModel } from './mchat/model-mchat'
import { getMChatAplicacaoPerguntasFalhas } from './mchat/utils-mchat'
import { MChatRfFieldModel } from './model-mChatRf'
import { isAccordionPreExpanded } from './utils-mChatRf'

interface MChatRfFieldProps {
  name: MetaPath<MChatRfFieldModel>
}

export function MChatRfField(props: Readonly<MChatRfFieldProps>) {
  const { name } = props

  const {
    input: { value: mChatValue },
  } = usePecField({ name: name.mchat })

  const {
    atendimentoProfissional: { iniciadoEm: dataAtendimento },
    cidadao: { dataNascimento },
    prontuario: { id: prontuarioId },
  } = useAtendimentoContext()

  const {
    data: { ultimoMChatR },
    loading,
  } = useUltimoMChatQuery({
    variables: {
      prontuarioId,
    },
  })

  const hasValuesAntendimentoAnterior = !loading && !isEmpty(ultimoMChatR)

  const valuesAtendimentoAnterior: MChatRfFieldModel = useMemo(
    () => (hasValuesAntendimentoAnterior ? convertMChatRfQueryToModel(ultimoMChatR) : null),
    [hasValuesAntendimentoAnterior, ultimoMChatR]
  )

  const { ageInMonths } = calculateAge(dataNascimento, dataAtendimento.getTime())
  const { ageInMonths: ageInMonthsAtendimentoAnterior } =
    hasValuesAntendimentoAnterior && calculateAge(dataNascimento, ultimoMChatR.dataResultado)

  const isMChatAnteriorRiscoBaixo = valuesAtendimentoAnterior?.mchat?.risco === MChatRiscoEnum.BAIXO
  const isMChatAnteriorRiscoElevado = valuesAtendimentoAnterior?.mchat?.risco === MChatRiscoEnum.ELEVADO
  const isMChatAtualRiscoElevado = mChatValue?.risco === MChatRiscoEnum.ELEVADO

  const hideConsultaSeguimento = isMChatAnteriorRiscoElevado || isMChatAtualRiscoElevado
  const perguntasParaAplicarConsultaSeguimento = getMChatAplicacaoPerguntasFalhas(mChatValue?.aplicacao)

  const mChatFooterInfo: MChatAnteriorFooterInfoModel = {
    dataResultado: ultimoMChatR?.dataResultado,
    lotacao: ultimoMChatR?.atendimentoProfissional?.lotacao,
    atendimentoProfissionalId: ultimoMChatR?.atendimentoProfissional?.id,
  }

  const isPreExpanded = isAccordionPreExpanded({
    ageInMonths,
    ageInMonthsAtendimentoAnterior,
    hasValuesAntendimentoAnterior,
    isMChatAnteriorRiscoBaixo,
  })

  if (loading) {
    return <LoadingIndicator message='Carregando dados do M-CHAT-R/F™...' />
  }

  return (
    <VFlow>
      <MChatRfAccordion isPreExpanded={isPreExpanded} isLoading={loading}>
        <VFlow vSpacing={0}>
          <MChatField
            name={name.mchat}
            valuesAtendimentoAnterior={valuesAtendimentoAnterior?.mchat}
            atendimentoAnteriorAtendProfId={ultimoMChatR?.atendimentoProfissional?.id}
            footerInfo={mChatFooterInfo}
          />
          {!hideConsultaSeguimento && (
            <ConsultaSeguimentoField
              name={name.consultaSeguimento}
              perguntasParaAplicar={perguntasParaAplicarConsultaSeguimento}
              valuesAtendimentoAnterior={valuesAtendimentoAnterior?.consultaSeguimento}
              mChatAtualRisco={mChatValue?.risco}
              mChatAnteriorRisco={valuesAtendimentoAnterior?.mchat?.risco}
            />
          )}
        </VFlow>
      </MChatRfAccordion>
    </VFlow>
  )
}
